import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import API, { formatApiErrorDetail, clearTabToken } from "@/lib/api";
import { analytics } from "@/instrument";

const AuthContext = createContext(null);

const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes HIPAA session timeout

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const timeoutRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // HIPAA: Auto-logout on inactivity
  const performTimeout = useCallback(async () => {
    if (user) {
      // Remember which portal the user was using so PatientLogin can bounce
      // staff back to /cliniclogin instead of showing the patient form.
      const role = user.role || "";
      const isStaff = ["admin", "prescriber", "customer_service", "it"].includes(role);
      sessionStorage.setItem("glpflo_last_portal", isStaff ? "staff" : "patient");
      try { await API.post("/auth/logout"); } catch { void 0; }
      clearTabToken();
      setUser(false);
      sessionStorage.setItem("glpflo_session_timeout", "1");
      // Hard-redirect so any staff-timeout lands on /cliniclogin and any
      // patient-timeout lands on /login — sidestepping whatever private route
      // the user was on when the timer fired.
      if (typeof window !== "undefined") {
        window.location.href = isStaff ? "/cliniclogin" : "/login";
      }
    }
  }, [user]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!user) return;

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    timeoutRef.current = setInterval(() => {
      if (Date.now() - lastActivityRef.current > SESSION_TIMEOUT_MS) {
        performTimeout();
      }
    }, 30000); // check every 30s

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      if (timeoutRef.current) clearInterval(timeoutRef.current);
    };
  }, [user, resetActivity, performTimeout]);

  // Identify the user in PostHog (with hashed id) whenever auth state flips
  // to a real user, and reset on logout. analytics.identify is a no-op when
  // the PostHog key is not configured.
  useEffect(() => {
    if (user && user._id) {
      analytics.identify(user._id, user.role);
    } else if (user === false) {
      analytics.reset();
    }
  }, [user]);

  const login = async (email, password) => {
    try {
      let captcha_token = null;
      try {
        const { executeCaptcha } = await import("@/lib/recaptcha");
        captcha_token = await executeCaptcha("login");
      } catch (_e) { /* captcha disabled — backend handles */ }
      const { data } = await API.post("/auth/login", { email, password, captcha_token, captcha_version: "v3" });
      if (data.mfa_required) {
        // Return the challenge so the calling page can render the MFA input.
        return { success: true, mfa_required: true, mfa_challenge: data.mfa_challenge };
      }
      setUser(data);
      sessionStorage.removeItem("glpflo_session_timeout");
      resetActivity();
      analytics.capture("login", { method: "password", role: data?.role || "unknown" });
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const verifyMfa = async (mfaChallenge, code) => {
    try {
      const { data } = await API.post("/auth/verify-mfa", { mfa_challenge: mfaChallenge, code });
      setUser(data);
      sessionStorage.removeItem("glpflo_session_timeout");
      resetActivity();
      analytics.capture("login", { method: "mfa", role: data?.role || "unknown" });
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const register = async (formData) => {
    try {
      const { data } = await API.post("/auth/register", formData);
      setUser(data);
      sessionStorage.removeItem("glpflo_session_timeout");
      resetActivity();
      return { success: true, data };
    } catch (e) {
      // Server-side waitlist gate — reload the page so the WaitlistGate
      // renders in place of the signup form. This is the belt-and-suspenders
      // fallback for the case where the frontend cache hadn't yet picked up
      // the toggle when the user hit submit.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail && detail.error === "waitlist_active") {
        window.location.reload();
        return { success: false, error: "waitlist_active", waitlist: true, message: detail.message };
      }
      return { success: false, error: formatApiErrorDetail(detail) };
    }
  };

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch { void 0; }
    clearTabToken();
    // Remember which portal was in use so the login-page redirect is right.
    if (user && user.role) {
      const isStaff = ["admin", "prescriber", "customer_service", "it"].includes(user.role);
      sessionStorage.setItem("glpflo_last_portal", isStaff ? "staff" : "patient");
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, verifyMfa }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
